import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ACTION_COLUMN_PREFIX } from "../constants";
/**
 *  @summary Utility to calculate how many columns can fit in a container.
 *  @param {array} widthArray  Array of integers representing with of vertical units: columns.
 *  @param {number} containerWidth  Width of the containing element.
 *  @returns {number} Number of columns to be returned.
 */

export const getDisplayableColumnCount = (widthArray, containerWidth, minimumColumnsRequired) => {
  let newWidth = 0,
      newAllowableColumns = 0;

  for (let i = 0; i < widthArray.length; i++) {
    if (newWidth < containerWidth && containerWidth - newWidth >= widthArray[i]) {
      newWidth += widthArray[i];
      newAllowableColumns++;
    } else {
      break;
    }
  }

  const minQualifiedColumns = newAllowableColumns <= minimumColumnsRequired ? minimumColumnsRequired : newAllowableColumns;
  return minQualifiedColumns;
};
/**
 *  @summary Utility function to calculate width of the columns.
 *  @param {array} columns  Array of nodes representing with of vertical units: columns.
 *  @returns {array} Array of column widths.
 */

export function getAllColumnsWidth(columns) {
  const columnWidths = [];

  for (let i = 0; i < columns.length; i++) {
    columnWidths.push(columns[i].offsetWidth);
  }

  return columnWidths;
}
/**
 *  @summary Utility function to flatten and attach order to each key passed in columnCollapseOrder prop.
 *   Each object within  has key as the `column name`, value as `order at which the column should collapse`.
 *  @param {integer} columnsLength count of original columns
 *  @param {array} collapseOrder array of table column keys  E.g : ['debitAccount', ['amount', 'beneficiary']]
 *  @returns {array} Flattened Collapse Order with group number.  [debitAccount: 5, amount: 4, beneficiary: 4]
 */

export function flattenCollapseOrder(columnsLength, collapseOrder) {
  let collapseOrderIndex = 0;
  const flattenCollapseOrder = [];

  while (collapseOrderIndex < collapseOrder.length) {
    const col = collapseOrder[collapseOrderIndex];
    const flattenedCollapseOrderIndex = columnsLength - collapseOrderIndex;

    if (!Array.isArray(col)) {
      flattenCollapseOrder[col] = flattenedCollapseOrderIndex;
    } else {
      col.forEach(innerCol => {
        flattenCollapseOrder[innerCol] = flattenedCollapseOrderIndex;
      });
    }

    collapseOrderIndex++;
  }

  return flattenCollapseOrder;
}
/**
 *  @summary Utility function to arrange columns by order mentioned by implementor.
 *  @param {array} columns  Array of objects representing columns in table.
 *  Notation :[{key:value}*n]
 *   E.g
 *      [{key: "id", label: "Payment ID",..},
 *      {key: "beneficiary", label: "Beneficiary"},
 *      {key: "amount", label: "Amount"}]
 *  @param {array} collapseOrder  Array of strings or string arrays representing the order of collapse.
 *  @returns {array} reorderedColumns Array of objects containing reordered columns.
 *  Notation : [{key:value,order: 0, originalIndex: 1}*n]
 *  E.g   [{key: "id", label: "Payment ID", order: 0, originalIndex: 1},
 *         {key: "beneficiary", label: "Beneficiary", order: 5, originalIndex: 3},
 *         {key: "amount", label: "Amount",order: 5, originalIndex: 4}]
 */

export function reorderColumnsByCollapseOrder(columns, flattenedCollapseOrder) {
  const reorderedOriginalColumns = [];
  const reorderedCollapsibleColumns = [];
  const keys = Object.keys(flattenedCollapseOrder); // Split collapsible columns from non collapsible columns in the original columns.

  const numberOfColumns = columns.length;

  for (let index = 0; index < numberOfColumns; index++) {
    const column = columns[index];
    const collapseOrderArrayIndex = flattenedCollapseOrder[column.key];

    if (keys.indexOf(column.key) > -1) {
      reorderedCollapsibleColumns.unshift(_objectSpread(_objectSpread({}, column), {}, {
        order: collapseOrderArrayIndex,
        originalIndex: index
      }));
    } else {
      reorderedOriginalColumns.push(_objectSpread(_objectSpread({}, column), {}, {
        order: collapseOrderArrayIndex || 0,
        originalIndex: index
      }));
    }
  } // Order collapsible columns, multiple columns can be in a  set with same group number.


  reorderedCollapsibleColumns.sort((a, b) => a.order - b.order); // Merge collapsible and non collapsible columns together.

  const reorderedColumns = [...reorderedOriginalColumns, ...reorderedCollapsibleColumns];
  return reorderedColumns;
}
/**
 *  @summary Utility function to  process column priority based on the order provided
 *  @param {array} columns  Array of nodes representing with of vertical units : columns
 *  @param {number} displayableColumnCount  Count of columns displayable in a viewport
 *  @param {array} reorderedColumnsByCollapseOrder  Array of  columns order by collapse order
 *  @param {array} flattenCollapseOrder   Array containing  flattened  columnCollapseOrder
 *  @param {number} minimumColumnRequired Minimum number of columns( action columns) need to be shown
 *  @returns {object} An object of  columnCount, collapsedColumns, orderedColumns, groupedCollapseOrder
 */

export function processColumnsOrder(columns, displayableColumnCount, reorderedColumnsByCollapseOrder, flattenCollapseOrder, minimumColumnRequired) {
  // Columns the implementor intends to show in the table.
  let displayableColumns = columns.slice(0),
      // Count of columns - cached for iteration.
  reorderedColumnCount = reorderedColumnsByCollapseOrder.length - 1,
      // Temp variable used for tracing continuity of columns in a group.
  previousCollapseOrder,
      // Temp variable used for calculating the count of displayable columns.
  temporaryDisplayableColumnsLength = displayableColumns.length; // Temp const used for keeping a track of indexes of  columns in collapse order.

  const originalIndexes = []; // Columns that need to be collapsed  after column order processing.

  const collapsedColumns = []; // Calculate the number of columns displayable with the collapse order.

  while (displayableColumnCount <= temporaryDisplayableColumnsLength) {
    const originalColumn = reorderedColumnsByCollapseOrder[reorderedColumnCount];
    const flattenCollapseOrderValue = originalColumn && flattenCollapseOrder[originalColumn.key]; // reduce the number of displayable column  if the column is a part of group

    if (flattenCollapseOrderValue === previousCollapseOrder && typeof flattenCollapseOrderValue !== "undefined" && typeof previousCollapseOrder !== "undefined") {
      displayableColumnCount = displayableColumnCount - 1;
    } else if (displayableColumnCount === temporaryDisplayableColumnsLength || minimumColumnRequired === temporaryDisplayableColumnsLength) {
      break;
    } // Re-set iteration indicator.


    temporaryDisplayableColumnsLength--; // Column should be collapsed , add to the array.

    collapsedColumns.push(originalColumn); // Keep a list of columns added to collapsible columns.

    originalIndexes.push(originalColumn.originalIndex); // Prepare for the next iteration , to evaluate if this column has a successor in the group.

    previousCollapseOrder = flattenCollapseOrderValue;
    reorderedColumnCount--;
  } // Columns to be displayed filtered from collapsed order.


  displayableColumns = displayableColumns.filter((column, index) => originalIndexes.indexOf(index) === -1);

  if (displayableColumnCount <= minimumColumnRequired) {
    displayableColumnCount = minimumColumnRequired;
  }

  return {
    displayableColumnCount,
    collapsedColumns,
    displayableColumns
  };
}
/**
 *  @summary Utility function to  calculate minimum columns required.
 *            i.e. Action columns(row numbers, expandable and sortable) + 1.
 */

export const calculateMinimumDisplayableColumns = columns => {
  // We know there are 3 actionable columns.
  const KNOWN_ACTIONABLE_COLUMNS = 3;
  let iteration;

  for (iteration = 0; iteration < KNOWN_ACTIONABLE_COLUMNS; iteration++) {
    const column = columns[iteration]; // All action columns start with "wf-" .

    if (column.key.indexOf(ACTION_COLUMN_PREFIX) === -1) {
      break;
    }
  }

  return iteration + 1;
};